import { useState } from 'react';
import {
    NSModal,
    NSButton,
    NSMultiSelect,
} from 'bricks';
import { useQueryClient } from '@tanstack/react-query';
import { OptionType } from 'bricks/NSSelectWithCheckboxes/NSSelectWithCheckboxes';
import { faArrowUpRightFromSquare } from '@fortawesome/pro-regular-svg-icons';
import { Link } from 'react-router-dom';
import { URLS } from 'services/urls';
import useToast from 'hooks/useToast';
import { useGetOrganizationCompanies } from 'components/DealForm/hooks/useGetOrganizationCompanies';
import { ICompany } from '../types';
import { CRM_ENTITY_TYPE } from '../constants';
import useUpsertCompanyAssociations from './hooks/useUpsertCompanyAssociations';
import getCRMDealCompaniesQueryOptions from './hooks/useGetCRMDealCompanies';

interface IUpsertProjectsModalProps {
    isOpen: boolean;
    toggle: () => void;
    organizationId: string;
    dealId: number;
    existingAssociations: ICompany[];
}

const UpsertCompaniesModal = ({
    isOpen, toggle, organizationId, dealId, existingAssociations,
}: IUpsertProjectsModalProps) => {
    const queryClient = useQueryClient();
    const { showSuccess, showError } = useToast();

    const {
        data: orgCompanies,
        isLoading: isLoadingOptions,
    } = useGetOrganizationCompanies();

    const [selected, setSelected] = useState<OptionType[]>(existingAssociations?.map(e => ({
        label: e.name,
        value: e.correlationId,
    })) || [] as OptionType[]);

    const {
        mutate: upsertAssociations,
        isPending: isSaving,
    } = useUpsertCompanyAssociations({
        onSuccess: () => {
            showSuccess('Companies associated successfully');
            queryClient.invalidateQueries(getCRMDealCompaniesQueryOptions(organizationId, dealId));
            toggle();
        },
        onError: error => {
            showError(`There was an error associating companies. Please try again. ${error?.message}`);
        },
    });

    const handleCreate = () => upsertAssociations({
        organizationId,
        associationType: CRM_ENTITY_TYPE.DEAL,
        associationId: dealId,
        companyCorrelationIds: selected.map(e => e.value),
    });

    const options = orgCompanies?.map(e => ({
        label: e.name,
        value: e.correlationId,
    })) || [];

    return (
        <NSModal
            isOpen={isOpen}
            toggle={toggle}
            modalHeaderText="Associate companies"
            modalHeaderClassName="bg-primary text-white"
            customFooter={(
                <div>
                    <NSButton
                        text="Cancel"
                        className="mr-2"
                        outline={false}
                        callback={toggle}
                    />
                    <Link
                        to={URLS.PROJECTS_APP.COMPANIES}
                        target="_blank"
                        rel="noreferrer"
                        className="cursor--pointer"
                    >
                        <NSButton
                            text="Create new company"
                            color="secondary"
                            outline={false}
                            icon={faArrowUpRightFromSquare}
                            className="heap-deal-activity-create-new-company"
                        />
                    </Link>
                    <NSButton
                        className="ml-2 heap-deal-activity-add-company"
                        text="Save"
                        color="primary"
                        outline={false}
                        callback={handleCreate}
                        isLoading={isSaving}
                    />
                </div>
            )}
        >
            <NSMultiSelect
                options={options}
                selectName="add-companies"
                label="Companies"
                value={selected}
                onChange={setSelected}
                isClearable={false}
                componentClassName="mt-1"
                isLoading={isLoadingOptions}
            />

        </NSModal>
    );
};

export default UpsertCompaniesModal;
